import { useEffect, useState } from 'react';
import { researchAPI } from '../utils/api';

// Phase A research flags for the signed-in user (GET /api/research/features).
//
// Every flag defaults to OFF and stays OFF on any error, on a teacher account
// (the server returns all false for non-students) and before the request
// resolves. The server enforces the same flags on every write, so this hook
// only decides what the UI shows; it is not the control itself.
//
// ui_support_words (item 15, proposal C) is a UI switch, not a research flag: it
// records nothing; like the research flags it is student-only on the server (a
// teacher is always OFF), and only the operator turns it on. The shared
// screen stays the same.
const FLAG_KEYS = [
  'instrumentation', 'micro_randomization', 'burden_probe', 'practice_mode',
  'ui_support_words',
];

export const RESEARCH_FEATURES_OFF = Object.freeze({
  instrumentation: false,
  micro_randomization: false,
  burden_probe: false,
  practice_mode: false,
  ui_support_words: false,
});

const normalize = (data) => {
  const next = {};
  FLAG_KEYS.forEach((key) => { next[key] = data?.[key] === true; });
  // All off (the common case): reuse the frozen constant so the state does not
  // change and the task view does not re-render during chart startup.
  if (FLAG_KEYS.every((key) => !next[key])) {
    return RESEARCH_FEATURES_OFF;
  }
  return next;
};

export function useResearchFeatures(userKey) {
  const [features, setFeatures] = useState(RESEARCH_FEATURES_OFF);

  useEffect(() => {
    let cancelled = false;
    setFeatures(RESEARCH_FEATURES_OFF);
    if (!userKey) return undefined;
    // Wrapped so a synchronous failure (e.g. a missing client) also lands in catch.
    Promise.resolve()
      .then(() => researchAPI.getFeatures())
      .then((response) => {
        if (cancelled) return;
        const next = normalize(response?.data);
        setFeatures((prev) => (
          FLAG_KEYS.every((key) => prev[key] === next[key]) ? prev : next
        ));
      })
      .catch(() => {
        if (!cancelled) setFeatures(RESEARCH_FEATURES_OFF);
      });
    return () => {
      cancelled = true;
    };
  }, [userKey]);

  return features;
}
